import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageCircle, Search, Inbox, MessageSquarePlus, Send, Loader,
  AlertCircle, X, User as UserIcon, Trash2, Check,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatRowStamp } from '../../utils/datetime';
import SmsThread from './SmsThread';

// Unified SMS inbox: a two-pane running log of EVERY text conversation in
// the facility. Left = conversation list (one row per customer, newest
// activity on top, unread badge) + a "New text" button. Right = either the
// selected thread (shared <SmsThread/>) with an inline reply box, or the
// "New text" composer for starting a fresh conversation to any number.
//
// Complements the per-customer Text tab — both render the same SmsThread.

// Re-poll the conversation list so new inbound texts surface without a
// manual refresh. Matches the thread's own poll cadence.
const LIST_POLL_MS = 25_000;

// Debounce the customer lookup as the operator types into the composer so we
// don't fire a request per keystroke.
const LOOKUP_DEBOUNCE_MS = 300;

// Row timestamps come from utils/datetime (formatRowStamp) so they parse the
// naive-UTC strings correctly and render in the facility's timezone.

const ConversationRow = ({ convo, active, onClick, onDelete, onMarkRead, timeZone }) => {
  const unread = convo.unread_count > 0;
  // The row root is a div (role=button) — not a <button> — so the hover-
  // revealed trash control can be a nested <button> without invalid markup.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`group relative w-full text-left px-3.5 py-3 border-b border-slate-100 transition-colors cursor-pointer ${
        active ? 'bg-blue-50/70' : 'hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2 pr-6">
        <span className={`text-[13px] truncate ${unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
          {convo.customer_name}
        </span>
        <span className="text-[10.5px] text-slate-400 flex-shrink-0">
          {formatRowStamp(convo.last_at, timeZone)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <span className={`text-[12px] truncate ${unread ? 'text-slate-700' : 'text-slate-400'}`}>
          {convo.last_direction === 'inbound' ? '' : 'You: '}
          {convo.last_message || '(no messages)'}
        </span>
        {/* The badge IS the control: the thing you want gone is the thing you
            click. Hover swaps the count for a tick. Marking read from here
            never opens the thread, so a reply you have already dealt with
            elsewhere can be cleared without reading it again. */}
        {unread && (
          <button
            type="button"
            title="Mark as read"
            aria-label={`Mark ${convo.customer_name} as read`}
            data-testid="sms-mark-read"
            onClick={(e) => { e.stopPropagation(); onMarkRead?.(); }}
            className="group/badge flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[10.5px] font-semibold transition-colors"
          >
            <span className="group-hover/badge:hidden">{convo.unread_count}</span>
            <Check className="hidden group-hover/badge:block h-3 w-3" strokeWidth={3} />
          </button>
        )}
      </div>
      {/* Hover-revealed delete — stops propagation so it doesn't open the row. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label={`Delete conversation with ${convo.customer_name}`}
        title="Delete conversation"
        className="absolute right-2 top-2 inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-opacity focus:opacity-100"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// Compose panel for starting a NEW text to any number. Lives in the right
// pane where "Select a conversation" otherwise shows. The operator can type
// a raw number, or type a name/number and pick a matching tenant from the
// suggestions (so the send threads onto that customer). On a successful send
// the parent refreshes the list and opens the resulting conversation.
const NewTextComposer = ({ facilityId, onClose, onSent, initialPhone = '', initialCustomer = null }) => {
  const [toPhone, setToPhone] = useState(initialCustomer?.phone || initialPhone || '');
  const [picked, setPicked] = useState(initialCustomer); // { id, full_name, phone }
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [looking, setLooking] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Lookup tenants by name/phone as the operator types (debounced). Skipped
  // once a tenant is picked — the input then shows the locked-in selection.
  useEffect(() => {
    if (picked) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setMatches([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLooking(true);
      try {
        const { data } = await apiClient.get(
          `/api/facilities/${facilityId}/customers/`,
          { params: { search: q, status: 'all' } },
        );
        if (!cancelled) {
          setMatches(Array.isArray(data) ? data.slice(0, 6) : []);
        }
      } catch {
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setLooking(false);
      }
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, picked, facilityId]);

  // Typing in the recipient field both sets the raw number to send to AND
  // drives the tenant search. Editing clears any prior pick.
  const onChangeRecipient = (e) => {
    const v = e.target.value;
    setToPhone(v);
    setQuery(v);
    if (picked) setPicked(null);
    setError(null);
  };

  const pickCustomer = (c) => {
    const id = c._id || c.id;
    setPicked({ id, full_name: c.full_name, phone: c.phone });
    setToPhone(c.phone || '');
    setQuery('');
    setMatches([]);
    setError(null);
  };

  const clearPick = () => {
    setPicked(null);
    setToPhone('');
    setQuery('');
  };

  const charCount = body.length;
  const counterClass =
    charCount > 320 ? 'text-red-600'
    : charCount > 160 ? 'text-amber-600'
    : 'text-slate-400';

  const handleSend = async () => {
    const phone = (toPhone || '').trim();
    if (!phone) {
      setError('Enter a phone number.');
      return;
    }
    if (!body.trim()) {
      setError('Type a message.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      const payload = { to_phone: phone, body };
      if (picked?.id) payload.customer_id = picked.id;
      const { data } = await apiClient.post(
        `/api/facilities/${facilityId}/sms/send`,
        payload,
      );
      // Hand the resolved customer_id back so the parent can refresh the list
      // and open that conversation. Number-only sends return null -> parent
      // just refreshes the list.
      onSent?.(data?.customer_id || null);
    } catch (err) {
      // 409 (provider unconfigured) / 502 (provider failed) / 400 surface the
      // backend detail; leave the composer intact so nothing looks sent.
      setError(err?.response?.data?.detail || 'Failed to send text.');
    } finally {
      setSending(false);
    }
  };

  const showSuggestions = !picked && matches.length > 0;

  return (
    <div className="max-w-xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquarePlus className="h-4 w-4 text-blue-600" />
          New text
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel new text"
          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-4 space-y-4">
        {/* Recipient — raw number or pick a matching tenant */}
        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            To
          </label>
          {picked ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2">
              <span className="inline-flex items-center gap-2 text-[13px] text-slate-800 min-w-0">
                <UserIcon className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                <span className="font-medium truncate">{picked.full_name}</span>
                <span className="text-slate-400 truncate">{picked.phone}</span>
              </span>
              <button
                type="button"
                onClick={clearPick}
                aria-label="Clear selected tenant"
                className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 flex-shrink-0"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={toPhone}
                onChange={onChangeRecipient}
                placeholder="Phone number or tenant name"
                autoComplete="off"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
              />
              {looking && (
                <Loader className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 animate-spin" />
              )}
              {showSuggestions && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden">
                  {matches.map((c) => (
                    <button
                      key={c._id || c.id}
                      type="button"
                      onClick={() => pickCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="text-[13px] font-medium text-slate-800 truncate">
                        {c.full_name}
                      </div>
                      <div className="text-[11.5px] text-slate-400 truncate">
                        {c.phone || 'No phone on file'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!picked && (
            <p className="text-[11px] text-slate-400 mt-1">
              Type a number to text anyone, or start typing a name to pick a tenant.
            </p>
          )}
        </div>

        {/* Message */}
        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => { setBody(e.target.value); setError(null); }}
            rows={4}
            placeholder="Type your message..."
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
          />
          <div className={`mt-1 text-[11px] ${counterClass}`}>
            {charCount} / 160 char{charCount === 1 ? '' : 's'}
            {charCount > 160 && (
              <span> · message will split into multiple SMS</span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          {error ? (
            <div className="inline-flex items-center gap-1.5 text-[12px] text-red-600">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
            </div>
          ) : <span />}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {sending ? (
              <><Loader className="h-3.5 w-3.5 animate-spin" /> Sending...</>
            ) : (
              <><Send className="h-3.5 w-3.5" /> Send</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SmsInbox = ({ facility, onReadChange }) => {
  const facilityId = facility?._id || facility?.id || '';
  // Render all timestamps in the facility's timezone (stored times are UTC).
  const facilityTz = facility?.timezone || undefined;
  const onReadChangeRef = useRef(onReadChange);
  onReadChangeRef.current = onReadChange;
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // Unmatched ("Unknown number") threads have no customer_id, so they're
  // selected by phone instead. At most one of selectedId / selectedPhone is
  // set at a time — they're mutually exclusive.
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [search, setSearch] = useState('');
  // When true, the right pane shows the "New text" composer instead of the
  // selected thread / empty state. composePrefill seeds it (a raw number from
  // an "Unknown number" reply, or a tenant picked from search).
  const [composing, setComposing] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  // Tenants matching the search who don't have a thread yet — so the operator
  // can start a new text by name, not just filter existing conversations.
  const [customerMatches, setCustomerMatches] = useState([]);

  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  const loadConversations = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await apiClient.get(
        `/api/facilities/${facilityId}/sms/conversations`,
      );
      setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
      setLoadError(null);
    } catch (err) {
      setLoadError(err?.response?.data?.detail || 'Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Initial load + poll so new inbound texts appear in the list.
  useEffect(() => {
    setLoading(true);
    loadConversations();
    const id = setInterval(loadConversations, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [loadConversations]);

  // Selecting a conversation marks it read (clears the badge) then refreshes
  // the list so the badge actually disappears.
  const selectConversation = useCallback(async (customerId) => {
    setComposing(false);
    setSelectedPhone(null);
    setSelectedId(customerId);
    if (!customerId || !facilityId) return;
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/sms/conversations/${customerId}/mark-read`,
      );
      // Let the parent (Communication section) refetch the Text-tab unread
      // badge so it drops immediately, not just on the next poll.
      onReadChangeRef.current?.();
    } catch {
      // Non-fatal — the badge clears on the next successful list poll anyway.
    }
    loadConversations();
  }, [facilityId, loadConversations]);

  // Clear a thread's badge WITHOUT opening it. Same endpoints the open path
  // uses, so there is one definition of "read" — this only skips selecting.
  // Works for a matched customer and for an unknown number alike.
  const markConversationRead = useCallback(async (convo) => {
    if (!facilityId || !convo) return;
    try {
      if (convo.customer_id) {
        await apiClient.post(
          `/api/facilities/${facilityId}/sms/conversations/${convo.customer_id}/mark-read`,
        );
      } else if (convo.phone) {
        await apiClient.post(
          `/api/facilities/${facilityId}/sms/unmatched/mark-read`,
          null,
          { params: { phone: convo.phone } },
        );
      } else {
        return;
      }
      onReadChangeRef.current?.();
    } catch {
      // Non-fatal — the badge clears on the next successful list poll anyway.
    }
    loadConversations();
  }, [facilityId, loadConversations]);

  // Open an unmatched ("Unknown number") thread by phone — the by-phone
  // mirror of selectConversation. Marks it read so its badge clears, then
  // refreshes the list. The right pane renders the SAME SmsThread, just keyed
  // on phone instead of customer_id.
  const selectUnmatched = useCallback(async (phone) => {
    setComposing(false);
    setSelectedId(null);
    setSelectedPhone(phone || '');
    if (!phone || !facilityId) return;
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/sms/unmatched/mark-read`,
        null,
        { params: { phone } },
      );
      onReadChangeRef.current?.();
    } catch {
      // Non-fatal — the badge clears on the next list poll anyway.
    }
    loadConversations();
  }, [facilityId, loadConversations]);

  // Delete an entire conversation. Pass customer_id when the row is a matched
  // thread, else the row's phone (unmatched "Unknown number" thread). Confirms
  // first, then refreshes the list and clears the selection if that thread was
  // open. Driven by the hover trash icon on each ConversationRow.
  const deleteConversation = useCallback(async (convo) => {
    if (!facilityId || !convo) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this entire conversation? This removes all its texts.')) {
      return;
    }
    const params = convo.customer_id
      ? { customer_id: convo.customer_id }
      : { phone: convo.phone || '' };
    try {
      await apiClient.delete(
        `/api/facilities/${facilityId}/sms/conversations`,
        { params },
      );
      // If the deleted thread was open, clear the right pane.
      if (convo.customer_id && convo.customer_id === selectedIdRef.current) {
        setSelectedId(null);
      }
      await loadConversations();
      // The Text-tab unread badge may have dropped (deleted unread inbound).
      onReadChangeRef.current?.();
    } catch (err) {
      setLoadError(err?.response?.data?.detail || 'Failed to delete conversation.');
    }
  }, [facilityId, loadConversations]);

  // After a successful new-text send: refresh the list, then open the
  // resulting conversation. A matched send returns the customer_id to open;
  // an unmatched (number-only) send returns null so we just refresh + close
  // the composer (the number-only thread surfaces under "Unknown number" but
  // has no clickable customer id to open).
  const handleSent = useCallback(async (customerId) => {
    setComposing(false);
    await loadConversations();
    if (customerId) {
      selectConversation(customerId);
    } else {
      setSelectedId(null);
      setSelectedPhone(null);
    }
  }, [loadConversations, selectConversation]);

  const startCompose = (prefill = null) => {
    setComposePrefill(prefill);
    setComposing(true);
    setSelectedId(null);
    setSelectedPhone(null);
  };

  // When searching, also look up matching tenants (debounced) so a name like
  // "John Spann" — who has no thread yet — surfaces as a "start new text" row.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setCustomerMatches([]); return undefined; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(
          `/api/facilities/${facilityId}/customers/`,
          { params: { search: q, status: 'all' } },
        );
        if (!cancelled) setCustomerMatches(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch {
        if (!cancelled) setCustomerMatches([]);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [search, facilityId]);

  const filtered = search.trim()
    ? conversations.filter((c) =>
        (c.customer_name || '').toLowerCase().includes(search.trim().toLowerCase())
        || (c.phone || '').includes(search.trim()))
    : conversations;

  // Tenants from the search who aren't already a conversation row.
  const newCustomerMatches = search.trim().length >= 2
    ? customerMatches.filter(
        (c) => !conversations.some((cv) => cv.customer_id === (c._id || c.id)))
    : [];

  // Resolve the open conversation from whichever selector is active. Guarded
  // so that when NOTHING is selected (both null) we return null rather than
  // accidentally matching an unmatched row via `c.customer_id === null`.
  const selected =
    selectedPhone != null
      ? (conversations.find((c) => !c.customer_id && (c.phone || '') === selectedPhone) || null)
      : selectedId
        ? (conversations.find((c) => c.customer_id === selectedId) || null)
        : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex" style={{ minHeight: '560px' }}>
        {/* Left: conversation list */}
        <div className="w-[300px] flex-shrink-0 border-r border-slate-200 flex flex-col">
          <div className="px-3.5 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-2.5">
              <MessageCircle className="h-4 w-4 text-blue-600" />
              <h3 className="text-[14px] font-semibold text-slate-900">Inbox</h3>
              <span className="text-[11px] text-slate-400 ml-auto">
                {loading ? '...' : `${total} conversation${total === 1 ? '' : 's'}`}
              </span>
            </div>
            <button
              type="button"
              onClick={startCompose}
              className={`w-full mb-2.5 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                composing
                  ? 'bg-blue-600 text-white'
                  : 'border border-blue-200 text-blue-700 hover:bg-blue-50'
              }`}
            >
              <MessageSquarePlus className="h-3.5 w-3.5" /> New text
            </button>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-md text-[12px] focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadError && (
              <div className="px-3.5 py-3 text-[12px] text-red-600">{loadError}</div>
            )}
            {loading ? (
              <div className="py-12 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {filtered.length === 0 && newCustomerMatches.length === 0 && (
                  <div className="px-4 py-12 text-center">
                    <Inbox className="h-7 w-7 text-slate-300 mx-auto mb-2" />
                    <p className="text-[12.5px] text-slate-400">
                      {conversations.length === 0
                        ? 'No text conversations yet.'
                        : 'No conversations match your search.'}
                    </p>
                  </div>
                )}
                {filtered.map((c) => (
                  <ConversationRow
                    key={c.customer_id || c.phone || '__unmatched__'}
                    convo={c}
                    timeZone={facilityTz}
                    onMarkRead={() => markConversationRead(c)}
                    active={!composing && (
                      c.customer_id
                        ? c.customer_id === selectedId
                        : selectedPhone != null && (c.phone || '') === selectedPhone
                    )}
                    onClick={() => (
                      c.customer_id
                        ? selectConversation(c.customer_id)
                        : selectUnmatched(c.phone || '')
                    )}
                    onDelete={() => deleteConversation(c)}
                  />
                ))}
                {/* Tenants without a thread yet — start a new text by name. */}
                {newCustomerMatches.length > 0 && (
                  <div>
                    <div className="px-3.5 pt-3 pb-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
                      Start a new text
                    </div>
                    {newCustomerMatches.map((c) => (
                      <button
                        key={c._id || c.id}
                        type="button"
                        onClick={() => startCompose({ customer: {
                          id: c._id || c.id, full_name: c.full_name, phone: c.phone,
                        } })}
                        className="w-full text-left px-3.5 py-2.5 border-b border-slate-100 hover:bg-slate-50"
                      >
                        <div className="text-[13px] font-medium text-slate-700 truncate">
                          {c.full_name}
                        </div>
                        <div className="text-[11.5px] text-slate-400 truncate">
                          {c.phone || 'No phone on file'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: composer, selected thread, or empty state */}
        <div className="flex-1 min-w-0 p-4 bg-slate-50/40">
          {composing ? (
            <NewTextComposer
              facilityId={facilityId}
              initialPhone={composePrefill?.phone || ''}
              initialCustomer={composePrefill?.customer || null}
              onClose={() => setComposing(false)}
              onSent={handleSent}
            />
          ) : selected ? (
            <div>
              <div className="mb-3">
                <h2 className="text-[15px] font-semibold text-slate-900">
                  {selected.customer_name}
                </h2>
                {!selected.customer_id && (
                  <p className="text-[12px] text-slate-400 mt-0.5">Unknown number</p>
                )}
              </div>
              <SmsThread
                key={selected.customer_id || `phone:${selected.phone}`}
                customerId={selected.customer_id || null}
                phone={selected.customer_id ? null : selected.phone}
                facilityId={facilityId}
                onActivity={loadConversations}
              />
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <MessageCircle className="h-9 w-9 text-slate-300 mb-3" />
              <p className="text-[13.5px] font-medium text-slate-500">
                Select a conversation
              </p>
              <p className="text-[12px] text-slate-400 mt-1 max-w-[260px]">
                Every text to and from your tenants shows up here. Pick a
                conversation on the left to read it and reply, or start a{' '}
                <button
                  type="button"
                  onClick={startCompose}
                  className="text-blue-600 hover:underline font-medium"
                >
                  new text
                </button>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmsInbox;
